import * as duration from 'duration-fns';
import _isEqual from 'lodash/isEqual';
import { format } from '@sravni/utils/lib/money';
import { Currency } from '../constants/currency.js';
import { TERMS } from '../constants/terms.js';
import { humanize } from './duration.js';
import { getSymbolByCode } from './getSymbolByCode.js';

const UNIT_TO_METHOD = {
    [TERMS.years]: duration.toYears,
    [TERMS.months]: duration.toMonths,
    [TERMS.days]: duration.toDays,
    [TERMS.minutes]: duration.toMinutes,
    [TERMS.hours]: duration.toHours,
    [TERMS.seconds]: duration.toSeconds,
};
const getFullUnitCount = (ISODuration, unit) => {
    const method = UNIT_TO_METHOD[unit];
    return method(ISODuration);
};
const humanizeTerm = humanize;
const humanizeTermBestEdge = (term) => {
    const { from, fromUnit, to, toUnit } = term;
    if (to && from && _isEqual(to, from)) {
        const edge = humanizeTerm(to, toUnit, { initialForm: true });
        return edge;
    }
    if (to) {
        const edge = humanizeTerm(to, toUnit, { initialForm: false });
        return `до ${edge}`;
    }
    if (from) {
        const edge = humanizeTerm(from, fromUnit, { initialForm: false });
        return `от ${edge}`;
    }
    return undefined;
};
const humanizeTermRange = (term, options, defaultText = 'Любой') => {
    const { from, fromUnit, to, toUnit } = term || {};
    if (from && to) {
        if (from === to) {
            return humanizeTerm(to, toUnit, options);
        }
        if (fromUnit === toUnit) {
            const left = getFullUnitCount(from, fromUnit);
            const right = humanizeTerm(to, toUnit, options);
            return options?.prepositions ? `от ${left} до ${right}` : `${left}–${right}`;
        }
        return options?.prepositions
            ? `от ${humanizeTerm(from, fromUnit, options)} до ${humanizeTerm(to, toUnit, options)}`
            : `${humanizeTerm(from, fromUnit, options)} – ${humanizeTerm(to, toUnit, options)}`;
    }
    if (from) {
        return `от ${humanizeTerm(from, fromUnit, options)}`;
    }
    if (to) {
        return `до ${humanizeTerm(to, toUnit, options)}`;
    }
    return defaultText;
};
const getTermBestEdgeString = (term, defaultText = 'На любой срок') => {
    const bestEdge = humanizeTermBestEdge(term);
    return bestEdge ? `На срок ${bestEdge}` : defaultText;
};
const getTermRangeToString = (term, defaultText = 'На любой срок') => {
    const range = humanizeTermRange(term);
    return range ? `${range}` : defaultText;
};
const convertToShortNumber = (value) => {
    const billion = 1000000000;
    const million = 1000000;
    const thousand = 1000;
    if (Math.round(value / million) >= 1000) {
        return `${(value / billion).toFixed()} млрд.`;
    }
    if (Math.round(value / thousand) >= 1000) {
        return `${(value / million).toFixed()} млн.`;
    }
    if (value >= thousand) {
        return `${(value / thousand).toFixed()} тыс.`;
    }
    return value.toString();
};
// AMOUNT
const getAmountString = (amount, currency, isFloat = false, isConvert = false) => {
    const symbol = currency ? `${getSymbolByCode(currency)}` : '';
    return `${isConvert ? convertToShortNumber(Number(amount)) : format(amount, isFloat)} ${symbol}`;
};
const getAmountRangeToString = (params) => {
    const { amountRange, currency, prefix = false, defaultText = 'Любая', isFloat, allowZero = false, isConvert = false, } = params;
    const { from, to } = amountRange || {};
    let output = '';
    const isFromExists = allowZero ? !!from || from === 0 : !!from;
    const isToExists = allowZero ? !!to || to === 0 : !!to;
    if (!isFromExists && !isToExists)
        return defaultText;
    if (isFromExists && isToExists && from !== to) {
        output = `${getAmountString(from, currency, isFloat, isConvert)} – ${getAmountString(to, currency, isFloat, isConvert)}`;
    }
    else if (isFromExists || isToExists) {
        output = `${getAmountString((from || to), currency, isFloat, isConvert)}`;
    }
    if (prefix) {
        if (isFromExists && !isToExists) {
            output = `от ${output}`;
        }
        else if (isToExists && !isFromExists) {
            output = `до ${output}`;
        }
    }
    return output;
};
const getRateString = (rate, fractionDigits = 2) => rate !== undefined ? `${parseFloat(rate.toFixed(fractionDigits))}%`.replace('.', ',') : '';
const getMinRateString = (rateRange, prefix = false, defaultText = '') => {
    const { from, to } = rateRange || {};
    let output = '';
    const isFromExists = !!from || from === 0;
    const isToExists = !!to || to === 0;
    if (isFromExists && isToExists && from !== to) {
        output = `${getRateString(from)}`;
    }
    else if (isFromExists || isToExists) {
        output = getRateString((isFromExists ? from : to));
    }
    else {
        return defaultText;
    }
    if (prefix) {
        if (isFromExists && isToExists && from !== to) {
            output = `от ${output}`;
        }
        else if (isFromExists && isToExists && from === to) {
            output = `${output}`;
        }
        else if (isFromExists && isToExists && from < to) {
            output = `от ${output}`;
        }
        else if (!isFromExists && isToExists) {
            output = `до ${output}`;
        }
        else if (isFromExists && !isToExists) {
            output = `от ${output}`;
        }
        else {
            output = `до ${output}`;
        }
    }
    return output;
};
const getRateRangeString = (rateRange, defaultText) => {
    const { from, to, fractionDigits } = rateRange || {};
    let output = '';
    const isFromExists = !!from || from === 0;
    const isToExists = !!to || to === 0;
    if (isFromExists && isToExists && from !== to) {
        output = `${getRateString(from, fractionDigits)} – ${getRateString(to, fractionDigits)}`;
    }
    else if (isFromExists && isToExists && from === to) {
        output = getRateString(from, fractionDigits);
    }
    else if (!isFromExists && isToExists) {
        output = `до ${getRateString(to, fractionDigits)}`;
    }
    else if (isFromExists && !isToExists) {
        output = `от ${getRateString(from, fractionDigits)}`;
    }
    else {
        output = defaultText;
    }
    return output;
};
const getHumanizedMaxValue = ({ from, to }, postfix = '') => {
    if (to) {
        return `до ${format(to)}${postfix}`;
    }
    if (from) {
        return `от ${format(from)}${postfix}`;
    }
    return 'Бесплатно';
};
const getHumanizedMinValue = ({ from, to, isFloat, isConvert = false }, postfix = '', defaultText = 'Бесплатно') => {
    if (from && to && from === to) {
        if (isConvert) {
            return `${convertToShortNumber(from)}${postfix}`;
        }
        return `${format(from, isFloat)}${postfix}`;
    }
    if (from) {
        if (isConvert) {
            return `от ${convertToShortNumber(from)}${postfix}`;
        }
        return `от ${format(from, isFloat)}${postfix}`;
    }
    if (to) {
        if (isConvert) {
            return `до ${convertToShortNumber(to)}${postfix}`;
        }
        return `до ${format(to, isFloat)}${postfix}`;
    }
    return defaultText;
};
const getTermUnitFromDictDate = (dictDate) => {
    let unit = TERMS.years;
    let duration = 'years';
    if (dictDate.includes('day')) {
        unit = TERMS.days;
        duration = 'days';
    }
    else if (dictDate.includes('month')) {
        unit = TERMS.months;
        duration = 'months';
    }
    return [unit, duration];
};
const getMaxAmount = (range, options, defaultText = 'По согласованию') => {
    const { from, to } = range;
    const { prefix = true, postfix = '', isConvert = false } = options;
    if (to) {
        const value = isConvert ? convertToShortNumber(to) : to;
        return `${prefix && 'до '}${value}${postfix}`;
    }
    if (from) {
        const value = isConvert ? convertToShortNumber(from) : from;
        return `${prefix && 'от '}${value}${postfix}`;
    }
    return defaultText;
};
// @ts-ignore
const generateTermRange = ({ dictDateFrom, dictDateTo, to, from, }) => {
    let fromUnit;
    let fromDuration;
    let toUnit;
    let toDuration;
    if (dictDateFrom) {
        [fromUnit, fromDuration] = getTermUnitFromDictDate(dictDateFrom);
    }
    if (dictDateTo) {
        [toUnit, toDuration] = getTermUnitFromDictDate(dictDateTo);
    }
    return {
        ...(from && fromDuration && { from: { [fromDuration]: from } }),
        ...(fromUnit && { fromUnit }),
        ...(to && toDuration && { to: { [toDuration]: to } }),
        ...(toUnit && { toUnit }),
    };
};
const humanizePriceRange = ({ priceRange, allowZero, defaultValue = '', }) => {
    const { from, to } = priceRange;
    const isFromExists = from;
    const isToExists = to;
    let output = defaultValue;
    if (isFromExists && isToExists && from !== to) {
        output = `${format(from)} – ${getAmountString(to, Currency.RUB, false)}`;
    }
    else if (isFromExists && isToExists && from === to) {
        output = getAmountString(from, Currency.RUB, false);
    }
    else if (isFromExists && !isToExists) {
        output = `от ${getAmountString(from, Currency.RUB, false)}`;
    }
    else if (!isFromExists && isToExists) {
        output = getAmountString(to, Currency.RUB, false);
    }
    return output;
};

export { convertToShortNumber, generateTermRange, getAmountRangeToString, getAmountString, getFullUnitCount, getHumanizedMaxValue, getHumanizedMinValue, getMaxAmount, getMinRateString, getRateRangeString, getRateString, getTermBestEdgeString, getTermRangeToString, getTermUnitFromDictDate, humanizePriceRange, humanizeTermBestEdge, humanizeTermRange };
//# sourceMappingURL=humanization.js.map
